// SPEC D-1113: スタッフの翌月以降の休み希望・有給希望(shift_requests)。
//   表と RLS は supabase/migrations/20261026190200_d1113_shift_requests.sql。
//   ■ 人は名簿(store_roster)の roster_id。共有アカウントなので画面で名前を選ぶ(選んだ名前は端末に覚える)。
//   ■ 出せるのは JST の翌月 1 日以降の日だけ(DB の RLS でも止める。ここと画面は先に止めるだけ)。
//   ■ 1 人 1 日 1 行(unique: store_code, roster_id, request_date)。出す = upsert、外した日 = 取り下げ(delete)。
//     承認・却下された日(status が submitted 以外)は直さない・消さない(DB も止める)。
//   ■ 他の人の希望は取らない(取得はいつも roster_id で絞る)。有給の残り日数は扱わない(D-1114 でマネージャーが見る)。
import { supabase } from '../lib/supabase'

export const REQUEST_KINDS = [
  { key: 'off', label: '休み', short: '休' },
  { key: 'paid_leave', label: '有給', short: '有' },
]

export const REQUEST_STATUS_LABELS = {
  submitted: '出した',
  approved: '承認',
  rejected: '却下',
}

export const NOTE_MAX = 200
const MONTH_REQUEST_LIMIT = 62 // 1 人 1 か月 = 最大 31 行。余裕を見て 2 か月分
const YM_RE = /^(\d{4})-(\d{2})-01$/
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

const pad2 = n => String(n).padStart(2, '0')

export function kindLabel(kind) {
  return REQUEST_KINDS.find(k => k.key === kind)?.label ?? null
}

/** JST の今日 'YYYY-MM-DD'(toISOString は使わない) */
export function todayJstDate(now = new Date()) {
  return now.toLocaleDateString('sv-SE', { timeZone: 'Asia/Tokyo' })
}

/** 'YYYY-MM-01' を delta か月ずらす */
export function addMonths(yearMonth, delta) {
  const m = YM_RE.exec(String(yearMonth ?? ''))
  if (!m) return null
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1 + delta, 1))
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-01`
}

/** 出せる最初の日 = JST の翌月 1 日('YYYY-MM-01')。DB の (date_trunc('month', now() at time zone 'Asia/Tokyo') + 1 month) と同じ */
export function openFromJst(now = new Date()) {
  const today = todayJstDate(now)
  return addMonths(`${today.slice(0, 7)}-01`, 1)
}

/** 画面で送れる一番先の月 = 翌々月(spec DESIGN 1) */
export function lastSelectableMonth(now = new Date()) {
  return addMonths(openFromJst(now), 1)
}

/** その月に希望を出せるか(翌月以降。翌々月より先は画面では出さない) */
export function isMonthOpen(yearMonth, now = new Date()) {
  if (!YM_RE.test(String(yearMonth ?? ''))) return false
  return yearMonth >= openFromJst(now) && yearMonth <= lastSelectableMonth(now)
}

/** その日に希望を出せるか(DB と同じく翌月 1 日以降) */
export function isDateOpen(date, now = new Date()) {
  return DATE_RE.test(String(date ?? '')) && date >= openFromJst(now)
}

/** 月の日の一覧 ['YYYY-MM-01', ...] */
export function monthDates(yearMonth) {
  const m = YM_RE.exec(String(yearMonth ?? ''))
  if (!m) return []
  const y = Number(m[1]); const mo = Number(m[2])
  const days = new Date(Date.UTC(y, mo, 0)).getUTCDate()
  return Array.from({ length: days }, (_, i) => `${m[1]}-${m[2]}-${pad2(i + 1)}`)
}

/** 7 列(日〜土)の月の表。空きは null。[[date|null x7], ...] */
export function monthGrid(yearMonth) {
  const dates = monthDates(yearMonth)
  if (dates.length === 0) return []
  const m = YM_RE.exec(yearMonth)
  const firstDow = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1)).getUTCDay()
  const cells = [...Array(firstDow).fill(null), ...dates]
  while (cells.length % 7 !== 0) cells.push(null)
  const weeks = []
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))
  return weeks
}

/** タップ 1 回の変化: 空 → 休み → 有給 → 空 */
export function cycleKind(kind) {
  if (!kind) return 'off'
  if (kind === 'off') return 'paid_leave'
  return null
}

/** picks = { 'YYYY-MM-DD': 'off' | 'paid_leave' } の日数 */
export function countPicks(picks) {
  let off = 0; let paid = 0
  for (const k of Object.values(picks ?? {})) {
    if (k === 'off') off += 1
    else if (k === 'paid_leave') paid += 1
  }
  return { off, paid }
}

/** 出した希望の行から、画面で直せる picks(submitted の行だけ)とメモを作る */
export function picksFromRows(rows) {
  const picks = {}
  let note = ''
  for (const r of rows ?? []) {
    if (r.status === 'submitted') picks[r.request_date] = r.kind
    if (!note && r.note) note = r.note
  }
  return { picks, note }
}

/**
 * 出すときの差分。rows = その人・その月の DB の行、picks = 画面の選び、note = メモ。
 *   upserts = 足す・直す行(承認・却下の日は入れない)/ deleteIds = 外した日(submitted の行だけ)
 *   今月以前の日・月の外の日・決まった日は入れない(DB でも止まる)。
 */
export function planSubmit({ storeCode, rosterId, yearMonth, rows, picks, note, now = new Date() }) {
  const inMonth = new Set(monthDates(yearMonth))
  const byDate = new Map((rows ?? []).map(r => [r.request_date, r]))
  const memo = String(note ?? '').trim().slice(0, NOTE_MAX) || null
  const upserts = []
  for (const [date, kind] of Object.entries(picks ?? {})) {
    if (!kind || !REQUEST_KINDS.some(k => k.key === kind)) continue
    if (!inMonth.has(date) || !isDateOpen(date, now)) continue
    const cur = byDate.get(date)
    if (cur && cur.status !== 'submitted') continue
    if (cur && cur.kind === kind && (cur.note ?? null) === memo) continue
    upserts.push({ store_code: storeCode, roster_id: rosterId, request_date: date, kind, note: memo, status: 'submitted' })
  }
  const deleteIds = (rows ?? [])
    .filter(r => r.status === 'submitted' && !picks?.[r.request_date] && isDateOpen(r.request_date, now))
    .map(r => r.request_id)
  return { upserts, deleteIds }
}

/** その人・その月の希望(他の人の分は取らない) */
export async function fetchShiftRequests(storeCode, rosterId, yearMonth) {
  const dates = monthDates(yearMonth)
  if (!storeCode || !rosterId || dates.length === 0) return []
  const { data, error } = await supabase
    .from('shift_requests')
    .select('request_id, roster_id, request_date, kind, note, status, decided_at')
    .eq('store_code', storeCode)
    .eq('roster_id', rosterId)
    .gte('request_date', dates[0])
    .lte('request_date', dates[dates.length - 1])
    .order('request_date', { ascending: true })
    .limit(MONTH_REQUEST_LIMIT)
  if (error) throw error
  return data ?? []
}

/** 出す(planSubmit の結果を流す)。{ upserted, deleted } */
export async function submitShiftRequests({ upserts, deleteIds }) {
  let upserted = 0; let deleted = 0
  if (upserts?.length) {
    // full-scan-intentional: 書いた行(1 人 1 か月 = 31 行まで)の request_id を返してもらうだけ。
    const { data, error } = await supabase
      .from('shift_requests')
      .upsert(upserts, { onConflict: 'store_code,roster_id,request_date' })
      .select('request_id')
    if (error) throw error
    upserted = data?.length ?? upserts.length
  }
  if (deleteIds?.length) {
    // full-scan-intentional: 消した行(1 人 1 か月 = 31 行まで)の request_id を返してもらうだけ。
    const { data, error } = await supabase
      .from('shift_requests')
      .delete()
      .in('request_id', deleteIds)
      .eq('status', 'submitted')
      .select('request_id')
    if (error) throw error
    deleted = data?.length ?? 0
  }
  return { upserted, deleted }
}

// ───────────────────────────────────────────────────────────
// SPEC D-1114: マネージャーがシフト表の翌月以降で希望を見る・決める。
//   ■ 取得は店の全員分(manager 用。roster_id で絞らない)。organization_id では絞らない(RLS で担保)。
//   ■ 決めるのは DB の関数 fn_shift_request_decide(supabase/migrations/20261026190300_d1114_shift_request_decide.sql)。
//     有給の承認で台帳(paid_leave_usages)に 1 行、承認から外すとその行を取り消す。どちらも関数の中の 1 トランザクション。
//   ■ 本番に表・関数がまだ無いときでも画面全体を落とさない(isMissingRelationError で見分けて、その欄だけ「—」にする)。

export const DECISIONS = ['approved', 'rejected', 'submitted']
const STORE_MONTH_REQUEST_LIMIT = 1000 // 1 店 1 か月 = 名簿 × 31 日まで。超えたら truncated

/** その店・その月の全員分の希望(manager 用)。{ rows, truncated } */
export async function fetchStoreMonthRequests(storeCode, yearMonth) {
  const dates = monthDates(yearMonth)
  if (!storeCode || dates.length === 0) return { rows: [], truncated: false }
  const { data, error } = await supabase
    .from('shift_requests')
    .select('request_id, roster_id, request_date, kind, note, status, decided_at')
    .eq('store_code', storeCode)
    .gte('request_date', dates[0])
    .lte('request_date', dates[dates.length - 1])
    .order('request_date', { ascending: true })
    .limit(STORE_MONTH_REQUEST_LIMIT)
  if (error) throw error
  const rows = data ?? []
  return { rows, truncated: rows.length >= STORE_MONTH_REQUEST_LIMIT }
}

/** 希望を決める(approved / rejected / submitted = 取り消して「出した」に戻す)。返り値は決めた後の行 */
export async function decideShiftRequest(requestId, decision) {
  if (!requestId) throw new Error('decideShiftRequest: requestId が必要')
  if (!DECISIONS.includes(decision)) throw new Error(`decideShiftRequest: decision が違う(${decision})`)
  const { data, error } = await supabase.rpc('fn_shift_request_decide', { p_request_id: requestId, p_decision: decision })
  if (error) throw error
  if (!data || typeof data !== 'object') throw new Error('決めた結果が返ってきませんでした')
  return data
}

/** 表・関数・列が DB にまだ無い(本番で migration の前)ときのエラーか */
export function isMissingRelationError(error) {
  const code = String(error?.code ?? '')
  return ['42P01', 'PGRST205', 'PGRST202', '42883', 'PGRST204'].includes(code)
}

/** 決めるのが失敗したときの画面の文 */
export function decideErrorMessage(error) {
  const msg = String(error?.message ?? error ?? '')
  if (/ERR-SREQ-DECIDE-004/.test(msg)) return '有給の台帳に未登録です(有給管理で名簿の人と結んでから承認してください)'
  if (/ERR-SREQ-DECIDE-005/.test(msg)) return 'その日の有給はもう有給の台帳にあります(二重には入れません)'
  if (/ERR-SREQ-DECIDE-001/.test(msg) || String(error?.code ?? '') === '42501') return '承認・却下できるのは社員以上です'
  if (/ERR-SREQ-DECIDE-003/.test(msg)) return 'この希望はもうありません(読み直してください)'
  if (isMissingRelationError(error)) return '承認の仕組みがまだ入っていません(司令塔の適用待ち)'
  return msg || '決められませんでした'
}

/** rows → Map('YYYY-MM-DD|roster_id' → row) */
export function indexRequests(rows) {
  const m = new Map()
  for (const r of rows ?? []) m.set(`${r.request_date}|${r.roster_id}`, r)
  return m
}

/** その月を「翌月を組む」表示にするか(manager 以上 かつ JST の翌月以降。D-1114 DESIGN 1) */
export function isPlanningMonth(yearMonth, isManager, now = new Date()) {
  const open = openFromJst(now)
  return !!isManager && !!open && YM_RE.test(String(yearMonth ?? '')) && yearMonth >= open
}

// ── 選んだ名前を端末に覚える(localStorage。読めない・書けない端末では毎回選ぶ)
const REMEMBER_KEY_PREFIX = 'rz_shift_request_roster:'

export function loadRememberedRoster(storeCode) {
  try {
    return window.localStorage.getItem(REMEMBER_KEY_PREFIX + storeCode) || null
  } catch {
    return null
  }
}

export function saveRememberedRoster(storeCode, rosterId) {
  try {
    if (rosterId) window.localStorage.setItem(REMEMBER_KEY_PREFIX + storeCode, rosterId)
    else window.localStorage.removeItem(REMEMBER_KEY_PREFIX + storeCode)
  } catch {
    // 書けない端末(プライベートモードなど)は覚えない
  }
}
